import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { IdeSession } from "~/api/opsTypes";
import { useCreateDispatch, useRevokeIdeSession } from "~/api/queries";
import {
  BindingChip,
  OnlineBadge,
  OnlineDot,
  PolicyBadge,
  VersionBadge,
} from "~/features/console/IdeBadges";
import { inboundPolicyOf } from "~/features/console/ideSession";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";
import { formatRelativeTime } from "~/foundation/utils/time";

export interface IdeSessionCardProps {
  session: IdeSession;
  advisedVersion: string | null;
  /** Admin view: a colleague's window is shown WITH their name, and without an Ask control. */
  showOwner?: boolean;
  canRevoke: boolean;
}

/**
 * One connected editor window.
 *
 * The Ask control is **absent** on a colleague's window rather than disabled (§E-4): dispatch
 * is per person and no admin override exists, so a disabled button would invite a support
 * ticket where an absent one states the design.
 */
export function IdeSessionCard({
  session,
  advisedVersion,
  showOwner = false,
  canRevoke,
}: IdeSessionCardProps) {
  const { t, i18n } = useTranslation();
  const [asking, setAsking] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const dispatch = useCreateDispatch();
  const revoke = useRevokeIdeSession();

  const canAsk = session.isOwn && session.isOnline;
  const windowName = session.workspaceName ?? t("Untitled workspace");
  const policy = inboundPolicyOf(session.capabilities);

  const send = () => {
    const text = prompt.trim();
    if (!text) {
      return;
    }
    dispatch.mutate(
      {
        kind: "thought",
        agent: "ClaudeCode",
        payload: { prompt: text },
        targetSessionKey: session.sessionKey,
      },
      {
        onSuccess: () => {
          setPrompt("");
          setAsking(false);
          toast.success(t("Sent to {{window}}", { window: windowName }));
        },
        onError: () => toast.error(t("Could not send the ask")),
      },
    );
  };

  return (
    <li className="flex flex-col gap-2 rounded-md border border-divider p-3">
      <div className="flex flex-wrap items-center gap-2">
        <OnlineDot online={session.isOnline} />
        <Link
          to="/console/ides/$sessionKey"
          params={{ sessionKey: session.sessionKey }}
          className="text-body font-medium text-text hover:underline"
        >
          {windowName}
        </Link>
        <OnlineBadge online={session.isOnline} />
        <PolicyBadge capabilities={session.capabilities} />
        <BindingChip session={session} />
        <VersionBadge version={session.extensionVersion} advised={advisedVersion} />
        {showOwner && !session.isOwn && session.userName ? (
          <span className="text-small text-text-secondary">{session.userName}</span>
        ) : null}
      </div>

      <p className="text-small text-text-tertiary">
        <bdi
          title={session.machineId ? t("Machine ID: {{id}}", { id: session.machineId }) : undefined}
        >
          {session.machineName ?? t("Unknown machine")}
        </bdi>
        {" · "}
        {t("extension {{version}}", { version: session.extensionVersion ?? "?" })}
        {session.bridgeVersion
          ? ` · ${t("bridge {{version}}", { version: session.bridgeVersion })}`
          : ""}
      </p>

      {/*
        `dir="ltr"` with a START-aligned ellipsis, and it is load-bearing: on an RTL page a
        path beginning "/" is reordered to the visual end, and the ellipsis then eats the
        START of the string — the half that says WHICH repo this window has open.
      */}
      {session.workspacePath ? (
        <p
          dir="ltr"
          data-testid="ide-workspace-path"
          className="truncate text-start text-small text-text-tertiary"
          title={session.workspacePath}
        >
          {session.workspacePath}
        </p>
      ) : null}

      <p className="text-small text-text-tertiary">
        {t("Last seen {{when}}", { when: formatRelativeTime(session.lastSeenAt, i18n.language) })}
      </p>

      {policy === "unknown" ? (
        <p className="text-small text-text-secondary">
          {t(
            "This window has not declared an inbound policy — it predates delegation, so an ask may never be claimed.",
          )}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {canAsk ? (
          <Button size="sm" variant="neutral" onClick={() => setAsking((open) => !open)}>
            {t("Ask this window")}
          </Button>
        ) : null}
        {canRevoke ? (
          <Button size="sm" variant="ghost" onClick={() => setConfirmingRevoke(true)}>
            {t("Revoke")}
          </Button>
        ) : null}
      </div>

      {asking && canAsk ? (
        <div className="flex items-center gap-2">
          <Input
            value={prompt}
            aria-label={t("What should this window do?")}
            placeholder={t("What should this window do?")}
            onChange={(event) => setPrompt(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button size="sm" disabled={dispatch.isPending || !prompt.trim()} onClick={send}>
            {dispatch.isPending ? t("Sending…") : t("Send")}
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmingRevoke}
        onOpenChange={setConfirmingRevoke}
        title={t("Revoke this IDE session?")}
        description={t("The window stops receiving work and must be reloaded to reconnect.")}
        confirmLabel={t("Revoke")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={revoke.isPending}
        onConfirm={() =>
          revoke.mutate(
            { id: session.id },
            {
              onSuccess: () => {
                setConfirmingRevoke(false);
                toast.success(t("IDE session revoked"));
              },
              onError: () => toast.error(t("Could not revoke the IDE session")),
            },
          )
        }
      />
    </li>
  );
}
